// Vue 3: Axios configuration for API calls
import axios from "axios";
import { normalizarErro } from "./apiError";
import { mostrarErro, avisarSessaoExpirada } from "./feedbackDeErro";

// Get API URL dynamically based on current window location
// Use env var if available, otherwise use relative URL (same origin)
const getApiBaseUrl = () => {
  // Check for environment variable first (set at build time)
  if (process.env.VUE_APP_API_ROOT) {
    return process.env.VUE_APP_API_ROOT;
  }
  // Check for global variable (can be set at runtime)
  if (typeof window !== 'undefined' && window.__VUE_APP_API_URL__) {
    return window.__VUE_APP_API_URL__;
  }
  // Default: use origin + /api/v1
  if (typeof window !== 'undefined') {
    return window.location.origin + '/api/v1';
  }
  return '/api/v1';
};

const API_URL = getApiBaseUrl();

axios.defaults.baseURL = typeof window !== 'undefined' 
  ? (process.env.VUE_APP_API_BASE || (typeof window !== 'undefined' ? window.location.origin : '') + '/')
  : (process.env.VUE_APP_API_BASE || "/");

// Axios with baseURL for API calls
const securedAxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  }
});

const plainAxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

// Axios WITHOUT baseURL for auth routes (/auth/*)
const authAxiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json"
  }
});

// Helper function for auth headers
function updateAuthHeaders(config) {
  const token = localStorage.default_auth_token;
  if (token) {
    const parts = token.split(";");
    // Don't set Content-Type for FormData (browser will set it with boundary)
    const isFormData = config.data instanceof FormData;
    config.headers = {
      ...config.headers,
      "Content-Type": isFormData ? undefined : "application/json",
      "token-type": parts[0] || null,
      "access-token": parts[1] || null,
      "client": parts[2] || null,
      "uid": parts[3] || null,
      "expiry": parts[4] || null
    };
  }
  return config;
}

// Interceptors for plainAxiosInstance (API calls)
plainAxiosInstance.interceptors.request.use(updateAuthHeaders);
plainAxiosInstance.interceptors.response.use(
  function(response) {
    if (response.headers["access-token"]) {
      const token = [
        response.headers["token-type"],
        response.headers["access-token"],
        response.headers["client"],
        response.headers["uid"],
        response.headers["expiry"]
      ].join(";");
      localStorage.default_auth_token = token;
    }
    return response;
  },
  tratarFalha
);

// Interceptors for authAxiosInstance (auth routes)
authAxiosInstance.interceptors.request.use(updateAuthHeaders);
authAxiosInstance.interceptors.response.use(
  function(response) {
    if (response.headers["access-token"]) {
      const token = [
        response.headers["token-type"],
        response.headers["access-token"],
        response.headers["client"],
        response.headers["uid"],
        response.headers["expiry"]
      ].join(";");
      localStorage.default_auth_token = token;
    }
    return response;
  },
  tratarFalha
);

/**
 * O tratamento central de falha.
 *
 * ------------------------------------------------------------- O QUE HAVIA
 *
 * As duas instâncias já tinham o segundo argumento do `use`, mas ele era
 * `function (error) { return Promise.reject(error) }` — um repasse que não faz
 * nada. O gancho estava montado e vazio, o que é pior do que não existir: dá a
 * impressão de que o caso está tratado.
 *
 * ------------------------------------------------- POR QUE NÃO ENGOLE O ERRO
 *
 * Continua rejeitando, e com o erro ORIGINAL. Engolir faria toda chamada
 * parecer bem-sucedida: o `.then()` seguinte rodaria com `undefined`, e a tela
 * mostraria sucesso sobre uma escrita que não aconteceu. O normalizado viaja
 * junto, em `error.appError`, para quem quiser reagir de forma específica.
 */
function tratarFalha(error) {
  const problema = normalizarErro(error);

  if (problema.exigeReautenticacao) {
    // A limpeza é aqui, e não na tela, porque um 401 pode chegar de qualquer
    // requisição de qualquer rota — inclusive de uma que o usuário nem está
    // olhando.
    delete localStorage.default_auth_token;
    avisarSessaoExpirada();
    irParaLogin();
  } else {
    mostrarErro(problema);
  }

  error.appError = problema;
  return Promise.reject(error);
}

/**
 * Leva ao login sem importar o router.
 *
 * Importar `../router` aqui criaria ciclo: o router carrega views, que
 * importam serviços, que importam este arquivo. `window.location` não tem esse
 * problema e, num caso de sessão morta, recarregar a aplicação inteira é
 * desejável — garante que nenhum estado da sessão anterior sobreviva em
 * memória.
 */
function irParaLogin() {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/login") return;
  window.location.assign("/login");
}

// A instância `secured` não é usada por nenhuma tela hoje, mas é exportada —
// registrar aqui evita que o primeiro uso futuro nasça sem tratamento.
securedAxiosInstance.interceptors.request.use(updateAuthHeaders);
securedAxiosInstance.interceptors.response.use(undefined, tratarFalha);

// Exports
export { plainAxiosInstance, securedAxiosInstance, authAxiosInstance };
export default { plainAxiosInstance };
